import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ResolveFn } from '@angular/router';

@Injectable()
export class LocalesService {
  obs: Observable<any>;

  availableLanguages: any[];
  availableTranslations: any[];
  arrayCodeIso: any[];
  private localeOptionsSubject = new BehaviorSubject({});
  private localeTranslationsSubject = new BehaviorSubject({});
  private localeAvailableByGameSubject = new BehaviorSubject({});

  constructor(private http: HttpClient, private translate: TranslateService) {
    translate.onLangChange.subscribe(() => {
      if (this.availableLanguages) {
        this.localeOptionsSubject.next({ options: this.getOptions() });
      }
      if (this.availableTranslations) {
        this.localeTranslationsSubject.next({ options: this.getTranslations() });
      }
      if (this.arrayCodeIso) {
        this.localeAvailableByGameSubject.next({
          options: this.getAvailableLocaleOptions(this.arrayCodeIso)
        });
      }
    });
  }

  LocalesResolver: ResolveFn<any> = (): Observable<any> | any => {
    if (this.availableLanguages) {
      return this.availableLanguages;
    }
    const fetchLocalesDefinitions = this.http.get('assets/data/languages.json').pipe(
      map((defs: any[]) => {
        this.availableTranslations = defs.map((df) => {
          const def = df.replace('.json', '');
          if (this.availableLanguages) {
            const defLang = this.availableLanguages.find((lang) => lang.code === def);
            if (defLang) {
              return defLang;
            }
          }
          return { code: def };
        });
        this.localeTranslationsSubject.next({ options: inject(LocalesService).getTranslations() });
        return defs;
      })
    );
    const fetchLocales = this.http.get('assets/data/locales.json').pipe(
      map((locales: any[]) => {
        this.availableLanguages = locales;
        this.localeOptionsSubject.next({ options: inject(LocalesService).getOptions() });
        return locales;
      })
    );

    this.obs = forkJoin(fetchLocalesDefinitions, fetchLocales);
    return this.obs;
  };

  getOptionsCollection(collection) {
    return this[collection]
      .map((language) => ({
        key: language.code,
        value: language.name
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  getOptions() {
    return this.getOptionsCollection('availableLanguages');
  }
  getOptionsObservable(): Observable<any> {
    return this.localeOptionsSubject.asObservable();
  }

  getTranslations() {
    return this.getOptionsCollection('availableTranslations');
  }
  getTranslationsObservable(): Observable<any> {
    return this.localeTranslationsSubject.asObservable();
  }

  getAvailableLocaleOptions(arrayCodeIso) {
    const arr = this.availableLanguages
      .filter((locale) => arrayCodeIso.indexOf(locale.code) > -1)
      .map((language) => ({
        key: language.code,
        value: language.name
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
    return arr;
  }
  getAvailableByGameObservable(): Observable<any> {
    this.arrayCodeIso = [];
    return this.localeAvailableByGameSubject.asObservable();
  }
  updateArrayCodeIso(arrayCodeIso) {
    this.arrayCodeIso = arrayCodeIso;
    this.localeAvailableByGameSubject.next({
      options: this.getAvailableLocaleOptions(this.arrayCodeIso)
    });
  }

  getLocale(codeIso) {
    return this.availableLanguages.find((locale) => locale.code === codeIso);
  }

  getLocaleName(codeIso) {
    if (!codeIso) {
      return '';
    }
    return this.getLocale(codeIso).name;
  }

  getLocaleNativeName(codeIso) {
    if (!codeIso) {
      return '';
    }
    return this.getLocale(codeIso).nativeName;
  }
}
